import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Sparkles,
    Wand2,
    Pin,
    PinOff,
    Loader2,
    Trash2,
    Eye,
    Bell,
    BellOff,
    Send,
    Edit3,
    Check,
    AlertCircle,
} from "lucide-react";
import api from "../../../lib/api";
import FileUpload from "../../FileUpload";

/**
 * Iter-30.20 — Pendant asso-side de AdminNewsImport :
 *   - Pas de dropdown "association cible" (auto-résolu via /asso/...)
 *   - Bouton libellé "Transformer" (sans mention d'IA pour rester sobre)
 *   - Même puissance Lia : parse email brut → drafts → publication groupée
 *
 * Branche sur :
 *   POST /asso/news/import/parse    (équivaut à /admin/.../import/parse)
 *   POST /asso/news/import/publish  (équivaut à /admin/.../import/publish)
 */
const CATEGORIES = [
    { value: "info", label: "Info", color: "bg-white/12 text-white" },
    { value: "campagne", label: "Campagne", color: "bg-shariti-orange/20 text-shariti-orange-light" },
    { value: "urgence", label: "Urgence", color: "bg-red-500/20 text-red-300" },
    { value: "alerte", label: "Alerte", color: "bg-yellow-500/20 text-yellow-300" },
    { value: "temoignage", label: "Témoignage", color: "bg-purple-500/20 text-purple-300" },
];

export default function AssoNewsImport() {
    const [rawText, setRawText] = useState("");
    const [parsing, setParsing] = useState(false);
    const [items, setItems] = useState(null);
    const [publishing, setPublishing] = useState(false);
    const [globalPushAll, setGlobalPushAll] = useState(false);

    const canParse = rawText.trim().length >= 40 && !parsing;
    const canPublish = items && items.length > 0 && !publishing;
    const charCount = rawText.length;

    const handleParse = async () => {
        if (!canParse) return;
        setParsing(true);
        try {
            const { data } = await api.post(
                "/asso/news/import/parse",
                { raw_text: rawText, fetch_og_images: true },
                { timeout: 90000 },
            );
            const got = data.items || [];
            if (!got.length) {
                toast.warning("Aucune actualité détectée dans ce texte", {
                    description: "Vérifie que le texte contient bien des actualités distinctes.",
                });
            } else {
                toast.success(
                    `${got.length} actualité${got.length > 1 ? "s" : ""} structurée${got.length > 1 ? "s" : ""}`,
                    { description: "Vérifie le contenu proposé, puis publie en lot." },
                );
            }
            setItems(got);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de la transformation. Réessaie.");
        } finally {
            setParsing(false);
        }
    };

    const handlePublish = async () => {
        if (!canPublish) return;
        const payload = {
            items: items.map((it) => ({
                title: it.title,
                body: it.body,
                category: it.category || "info",
                attachment_url: it.attachment_url || null,
                image_url: it.image_url || null,
                pinned: !!it.pinned,
                notify_push: globalPushAll ? true : !!it.notify_push,
            })),
        };
        setPublishing(true);
        try {
            const { data } = await api.post("/asso/news/import/publish", payload, {
                timeout: 60000,
            });
            // Iter-30.22 — Affiche le message backend (gère le cas mixte
            // nouvelles + mises à jour de doublons existants).
            toast.success(
                data.message ||
                    `${data.inserted_count} actualité${data.inserted_count > 1 ? "s" : ""} publiée${
                        data.inserted_count > 1 ? "s" : ""
                    }`,
                {
                    description: data.push_count
                        ? `Notif envoyée à ${data.push_count} ambassadeur${data.push_count > 1 ? "s" : ""}.`
                        : "Visibles sur ton espace partenaire dès maintenant.",
                    duration: 6000,
                },
            );
            setItems(null);
            setRawText("");
            setGlobalPushAll(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur de publication.");
        } finally {
            setPublishing(false);
        }
    };

    const updateItem = (idx, patch) => {
        setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    };
    const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
    const togglePin = (idx) => {
        setItems((prev) =>
            prev.map((it, i) => (i === idx ? { ...it, pinned: !it.pinned } : { ...it, pinned: false })),
        );
    };

    const totalPinned = useMemo(() => (items || []).filter((it) => it.pinned).length, [items]);

    return (
        <div className="space-y-5" data-testid="asso-news-import">
            {/* HERO */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-3xl p-5 sm:p-6 relative overflow-hidden"
            >
                <div className="absolute inset-0 bg-gradient-to-br from-shariti-orange/8 to-transparent pointer-events-none" />
                <div className="relative flex items-start gap-4">
                    <div className="shrink-0 w-11 h-11 rounded-2xl bg-shariti-orange/15 flex items-center justify-center border border-shariti-orange/30">
                        <Sparkles className="w-5 h-5 text-shariti-orange-light" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-extrabold text-white mb-1">
                            Importer une actualité en un copier-coller
                        </h3>
                        <p className="text-xs sm:text-sm text-white/55 leading-snug">
                            Colle ici l&apos;email brut de ta newsletter (incluant signature, liens, paragraphes).
                            On extrait chaque actu, on rédige un titre + résumé prêts à publier sur ton espace partenaire.
                        </p>
                    </div>
                </div>
            </motion.div>

            {/* STEP 1 — Email brut */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="glass-card rounded-3xl p-5 sm:p-6"
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-7 h-7 rounded-full bg-shariti-orange/20 flex items-center justify-center text-shariti-orange-light text-xs font-extrabold">
                        1
                    </div>
                    <h4 className="text-base font-extrabold text-white">Colle ton email brut</h4>
                </div>
                <label className="block text-[10px] font-thin tracking-[0.22em] uppercase text-white/45 mb-2">
                    Email brut — copie-colle tout le contenu
                </label>
                <textarea
                    value={rawText}
                    onChange={(e) => setRawText(e.target.value)}
                    placeholder="Colle ici l'email reçu (signature, liens, paragraphes — tout sera trié automatiquement)..."
                    data-testid="asso-news-import-textarea"
                    className="w-full min-h-[240px] bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white placeholder-white/30 focus:border-shariti-orange/50 focus:outline-none resize-y transition-all"
                />
                <div className="flex items-center justify-between mt-2 text-[10px] tracking-[0.18em] uppercase text-white/45">
                    <span>{charCount} caractère{charCount > 1 ? "s" : ""} · min 40</span>
                    {charCount > 30000 && (
                        <span className="text-red-400 inline-flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Texte trop long (max 30 000)
                        </span>
                    )}
                </div>
                <div className="flex items-center justify-end gap-3 mt-4 flex-wrap">
                    <button
                        onClick={handleParse}
                        disabled={!canParse}
                        data-testid="asso-news-import-transform-btn"
                        className={`inline-flex items-center gap-2 font-extrabold py-2.5 px-5 rounded-2xl text-sm transition-all ${
                            canParse
                                ? "bg-shariti-orange text-white orange-glow hover:bg-shariti-orange-light"
                                : "bg-white/5 text-white/30 cursor-not-allowed"
                        }`}
                    >
                        {parsing ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Transformation en cours...
                            </>
                        ) : (
                            <>
                                <Wand2 className="w-4 h-4" />
                                Transformer
                            </>
                        )}
                    </button>
                </div>
            </motion.div>

            {/* STEP 2 — Drafts */}
            <AnimatePresence>
                {items && items.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="glass-card rounded-3xl p-5 sm:p-6 space-y-4"
                    >
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-3">
                                <div className="w-7 h-7 rounded-full bg-shariti-orange/20 flex items-center justify-center text-shariti-orange-light text-xs font-extrabold">
                                    2
                                </div>
                                <h4 className="text-base font-extrabold text-white">
                                    Relis et publie ({items.length} actualité{items.length > 1 ? "s" : ""})
                                </h4>
                            </div>
                            <label className="inline-flex items-center gap-2 text-xs text-white/65 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={globalPushAll}
                                    onChange={(e) => setGlobalPushAll(e.target.checked)}
                                    data-testid="asso-news-import-pushall"
                                    className="accent-shariti-orange w-3.5 h-3.5"
                                />
                                Notifier tous les ambassadeurs
                            </label>
                        </div>

                        <div className="space-y-3">
                            {items.map((it, idx) => (
                                <ItemRow
                                    key={idx}
                                    idx={idx}
                                    item={it}
                                    onChange={(patch) => updateItem(idx, patch)}
                                    onRemove={() => removeItem(idx)}
                                    onTogglePin={() => togglePin(idx)}
                                />
                            ))}
                        </div>

                        {totalPinned > 0 && (
                            <p className="text-[11px] text-shariti-orange-light tracking-wider">
                                <Pin className="w-3 h-3 inline mr-1" /> 1 actu sera épinglée en tête.
                            </p>
                        )}

                        <div className="pt-2 flex items-center justify-between gap-3 flex-wrap border-t border-white/5">
                            <button
                                onClick={() => setItems(null)}
                                className="text-xs text-white/45 hover:text-white/75 transition-colors"
                                data-testid="asso-news-import-discard"
                            >
                                Tout annuler
                            </button>
                            <button
                                onClick={handlePublish}
                                disabled={!canPublish}
                                data-testid="asso-news-import-publish-btn"
                                className={`inline-flex items-center gap-2 font-extrabold py-2.5 px-5 rounded-2xl text-sm transition-all ${
                                    canPublish
                                        ? "bg-shariti-orange text-white orange-glow hover:bg-shariti-orange-light"
                                        : "bg-white/5 text-white/30 cursor-not-allowed"
                                }`}
                            >
                                {publishing ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Publication...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Publier tout
                                    </>
                                )}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function ItemRow({ idx, item, onChange, onRemove, onTogglePin }) {
    const [editing, setEditing] = useState(false);
    const cat = CATEGORIES.find((c) => c.value === item.category) || CATEGORIES[0];

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.04 }}
            className="rounded-2xl bg-white/5 border border-white/10 p-3 sm:p-4"
            data-testid={`asso-news-import-item-${idx}`}
        >
            <div className="flex items-start gap-3">
                {item.image_url && (
                    <img
                        src={item.image_url}
                        alt=""
                        className="shrink-0 w-16 h-16 rounded-xl object-cover"
                    />
                )}
                <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span
                            className={`text-[9px] font-extrabold tracking-[0.18em] uppercase px-1.5 py-0.5 rounded-full ${cat.color}`}
                        >
                            {cat.label}
                        </span>
                        <button
                            onClick={onTogglePin}
                            data-testid={`asso-news-import-pin-${idx}`}
                            className={`p-1 rounded-md transition-colors ${
                                item.pinned ? "text-shariti-orange" : "text-white/30 hover:text-white/60"
                            }`}
                            title={item.pinned ? "Désépingler" : "Épingler en haut"}
                        >
                            {item.pinned ? <Pin className="w-3.5 h-3.5" /> : <PinOff className="w-3.5 h-3.5" />}
                        </button>
                        <button
                            onClick={() => onChange({ notify_push: !item.notify_push })}
                            data-testid={`asso-news-import-notify-${idx}`}
                            className={`p-1 rounded-md transition-colors ${
                                item.notify_push ? "text-shariti-orange" : "text-white/30 hover:text-white/60"
                            }`}
                            title={item.notify_push ? "Notif activée" : "Notif désactivée"}
                        >
                            {item.notify_push ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                        </button>
                        <div className="ml-auto flex items-center gap-1">
                            <button
                                onClick={() => setEditing(!editing)}
                                className="p-1.5 rounded-md text-white/40 hover:text-white hover:bg-white/5"
                                data-testid={`asso-news-import-edit-${idx}`}
                                title={editing ? "Fermer l'édition" : "Éditer"}
                            >
                                {editing ? <Check className="w-3.5 h-3.5" /> : <Edit3 className="w-3.5 h-3.5" />}
                            </button>
                            <button
                                onClick={onRemove}
                                className="p-1.5 rounded-md text-red-300/60 hover:text-red-300 hover:bg-red-500/10"
                                data-testid={`asso-news-import-remove-${idx}`}
                                title="Retirer"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>

                    {editing ? (
                        <div className="space-y-2">
                            <input
                                type="text"
                                value={item.title}
                                onChange={(e) => onChange({ title: e.target.value })}
                                data-testid={`asso-news-import-title-${idx}`}
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white font-bold"
                                placeholder="Titre de l'actualité"
                            />
                            <textarea
                                value={item.body}
                                onChange={(e) => onChange({ body: e.target.value })}
                                data-testid={`asso-news-import-body-${idx}`}
                                rows={4}
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white/80 resize-y"
                                placeholder="Corps de l'actualité"
                            />
                            <div className="grid grid-cols-2 gap-2">
                                <select
                                    value={item.category}
                                    onChange={(e) => onChange({ category: e.target.value })}
                                    data-testid={`asso-news-import-cat-${idx}`}
                                    className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs text-white"
                                >
                                    {CATEGORIES.map((c) => (
                                        <option key={c.value} value={c.value} className="bg-shariti-deep-black">
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    value={item.attachment_url || ""}
                                    onChange={(e) => onChange({ attachment_url: e.target.value || null })}
                                    data-testid={`asso-news-import-link-${idx}`}
                                    className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs text-white"
                                    placeholder="Lien externe (optionnel)"
                                />
                            </div>
                            <FileUpload
                                value={item.image_url}
                                onChange={(url) => onChange({ image_url: url })}
                                accept="image/*"
                                kind="news"
                                label="Image (optionnel — remplace l'OG image)"
                            />
                        </div>
                    ) : (
                        <>
                            <p className="text-sm font-extrabold text-white leading-tight">{item.title}</p>
                            <p className="text-xs text-white/65 leading-relaxed line-clamp-3">{item.body}</p>
                            {item.attachment_url && (
                                <a
                                    href={item.attachment_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-[10px] text-shariti-orange-light tracking-wider hover:underline"
                                >
                                    <Eye className="w-3 h-3" />
                                    Lien attaché
                                </a>
                            )}
                        </>
                    )}
                </div>
            </div>
        </motion.div>
    );
}
